import {
    EventCardActions,
    EventCardBadge,
    EventCardDate,
    EventCardDetails,
    EventCardImage,
    EventCardLink,
    EventCardLocation,
    EventCardPrice,
    EventCardPromoted,
    EventCardSaveAction,
    EventCardShareAction,
    EventCardTitle,
    EventCardTracking,
    EventCardUrgencySignals,
    FormattedEvent,
    getEventCardLinkProps,
    getShareActionProps,
    HorizontalEventCard,
    HorizontalEventCardColumn,
    HORIZONTAL_EVENT_CARD_IMAGE_CONFIG,
    HORIZONTAL_LEFT_CARD_BADGE_STYLE,
    useFormattedLocationString,
    useFormattedSalesStatus,
    useGetFormattedEventDateTimeString,
    useMinPriceString,
    usePriceRangeString,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import React from 'react';

import { useGetStatsigEnrollment } from '../../../../hooks';
import { SearchCardDesktopExperiment } from '../../../../types/Experiments';

import { DiscoverEventCardProps } from '../../types';
import '../DiscoverHorizontalEventCard/DiscoverHorizontalEventCard.scss';

const MOBILE_WIDTH = 480;
const useWindowWidthAtMount = (): number => {
    const [windowWidth, setWindowWidth] = React.useState<number>(0);

    React.useEffect(() => {
        setWindowWidth(window.innerWidth);
    }, []);

    return windowWidth;
};

const usePriceStringExperiment = (event: FormattedEvent, locale: string) => {
    //Temporarily adding both hooks for desktop price string experiment
    //Remove losing format for cleanup https://eventbrite.atlassian.net/browse/EB-223497
    const windowWidth = useWindowWidthAtMount();
    const isTabletOrDesktopWidth = windowWidth > MOBILE_WIDTH;

    const desktopPriceStringFormat = useGetStatsigEnrollment({
        name: SearchCardDesktopExperiment.name,
        paramName: SearchCardDesktopExperiment.paramName,
        defaultValue: SearchCardDesktopExperiment.values.control,
        enabled: isTabletOrDesktopWidth,
    }).variant;

    const formattedPriceRangeString = usePriceRangeString({
        minPrice: event?.minPrice,
        locale: locale,
        maxPrice: event?.maxPrice,
    });
    const formattedPriceString = useMinPriceString({
        price: event?.minPrice,
        locale: locale,
        minPriceStringFormat: 'from',
    });

    const preformattedPriceString =
        desktopPriceStringFormat === SearchCardDesktopExperiment.values.variant
            ? formattedPriceString
            : formattedPriceRangeString;

    return preformattedPriceString;
    //End Cleanup Section
};

export const DiscoverHorizontalEventCardVariantFour: React.FunctionComponent<
    DiscoverEventCardProps
> = (props) => {
    const {
        event,
        locale,
        isRaised,
        statsigLocationString,
        affCode = 'ebdssbdestsearch',
        moreActions,
    } = props;
    const { urgencySignals } = event;

    const linkProps = getEventCardLinkProps({
        affCode: affCode,
        url: event?.url,
        isTargetBlank: true,
        isNoFollow: event.ticketsBy !== 'Eventbrite',
        onClick: () => props.onClick?.(),
        label: gettext('View %(eventName)s', { eventName: event.name }),
    });

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event?.startDate,
        startTime: event?.startTime,
        timezone: event?.timezone,
        repeatingInstanceCount: event?.repeatingInstanceCount,
        locale: locale,
        useUserTimezone: event.isOnlineEvent,
        dateTimeDelimeter: '•',
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        style: 'venue',
    });

    const formattedPriceString = usePriceStringExperiment(event, locale);

    //Only show active Sales Status (sold out etc)
    //For non-repeating events to prevent incorrect signals.
    const formattedSalesStatus = useFormattedSalesStatus(
        event.eventSalesStatus,
        (event?.repeatingInstanceCount || 0) > 0,
    );

    const shouldShowUrgencySignals = !!urgencySignals && !formattedSalesStatus;

    const eventCardTrackingProps = {
        statsigLocationString: statsigLocationString,
        eventData: {
            isPromoted: !!event.isPromoted,
            isFree: event.isFree,
            isOnlineEvent: event.isOnlineEvent,
            hasUrgencySignals: shouldShowUrgencySignals,
            id: event.id,
            venue: event.venue,
            tags: event.tags,
        },
    };

    return (
        <section className="discover-horizontal-event-card">
            <HorizontalEventCard isRaised={isRaised} actionVisibility="hidden">
                <EventCardTracking {...eventCardTrackingProps}>
                    <EventCardBadge style={HORIZONTAL_LEFT_CARD_BADGE_STYLE}>
                        {formattedSalesStatus}
                    </EventCardBadge>
                    <HorizontalEventCardColumn
                        tokens={{
                            '--HorizontalCardColumnMarginRight': '24px',
                        }}
                    >
                        {shouldShowUrgencySignals && (
                            <EventCardUrgencySignals
                                className="discover-horizontal-event-card__urgency-signals--left"
                                urgencySignals={urgencySignals}
                            />
                        )}
                        <EventCardLink {...linkProps}>
                            <EventCardImage
                                name={event.name}
                                loading={props.isLazyImage ? 'lazy' : 'eager'}
                                tokens={{
                                    ...HORIZONTAL_EVENT_CARD_IMAGE_CONFIG,
                                    '--image-background-color': event.edgeColor,
                                }}
                                src={event.imageUrl}
                            />
                        </EventCardLink>
                    </HorizontalEventCardColumn>
                    <EventCardDetails
                        tokens={{
                            '--EventCardDetailsPadding': '0 16px 0 0',
                            '--EventCardDetailsFlexGrow': '2',
                        }}
                    >
                        <EventCardPromoted
                            isPromoted={event.isPromoted}
                            tokens={{ '--PromotedPaddingBottom': '4px' }}
                        />
                        <EventCardLink {...linkProps}>
                            <EventCardTitle
                                className="event-card__clamp-line--two"
                                title={event.name}
                            />
                        </EventCardLink>
                        <EventCardDate
                            style={{ marginTop: '4px' }}
                            className="event-card__clamp-line--one eds-text-color--ui-600"
                            formattedDateString={formattedDateString}
                            variant="body-md"
                        />
                        <EventCardLocation
                            style={{ marginTop: '4px' }}
                            className="event-card__clamp-line--one eds-text-color--ui-600"
                            formattedLocationString={formattedLocationString}
                        />
                        <EventCardPrice
                            style={{ marginTop: '8px' }}
                            isFree={event.isFree}
                            formattedPriceString={formattedPriceString}
                            variant="body-md-bold"
                        />

                        <EventCardActions
                            tokens={{
                                '--EventCardActionsMargin': '12px 0 0 0',
                            }}
                        >
                            <EventCardShareAction
                                {...getShareActionProps({
                                    name: event.name,
                                    id: event.id,
                                    url: event.url,
                                    affCode: affCode,
                                })}
                            />
                            <EventCardSaveAction
                                isAuthenticated={!!props.isAuthenticated}
                                savedByYou={event.savedByYou}
                                id={event.id}
                                eventName={event.name}
                                imageUrl={event.imageUrl}
                            />
                            {moreActions}
                        </EventCardActions>
                    </EventCardDetails>
                </EventCardTracking>
            </HorizontalEventCard>
        </section>
    );
};
